import React, { useEffect, useRef, useState } from "react";
import { X, Upload, ChevronLeft, ChevronRight, Loader2, ExternalLink, MessageCircle, Sparkles, Check, RotateCcw, GitCompareArrows, Download, Film, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { lockBodyScroll } from "@/lib/lockBodyScroll";
import { thumbUrl } from "@/lib/thumb";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin lightbox for the AI photoreeks. Click any photo → opens fullscreen with
 * the related annotation-feedback comments visible (with the red drawing
 * overlay) and a Vervangen button. Replacing uploads a new file to /api/upload,
 * then POSTs to /api/requests/:id/ai-photos/:i/replace which swaps the URL in
 * `ai_photo_urls` and deletes the feedback comments for that photo.
 *
 * Props:
 *   - requestId       : string
 *   - photos          : string[] (ai_photo_urls)
 *   - openIndex       : number | null (null = closed)
 *   - onClose         : () => void
 *   - onReplaced      : (newUrl, index) => void   — caller refreshes data
 *   - commentsByPhoto : { [photoIdx]: comment[] }  — to display per photo
 */
export default function AdminAiPhotoLightbox({
    requestId,
    photos,
    openIndex,
    onClose,
    onReplaced,
    commentsByPhoto = {},
    previousUrls = {},
    reelUrls = {},
    reelOriginalUrls = {},
    addressLabel = "",
    onOpenVideoGallery,
    onOpenAiHistory,
}) {
    const [idx, setIdx] = useState(openIndex);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0); // 0..100
    const [dropActive, setDropActive] = useState(false);
    const [showAnnotations, setShowAnnotations] = useState(true);
    const fileInputRef = useRef(null);

    // Build a friendly filename for the AI download buttons. Slug-safe so it
    // works across macOS/Windows downloads folders.
    const slugify = (s) => (s || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s.-]/g, "")
        .replace(/\s+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 60) || "foto";
    const downloadName = (variant) => {
        const addr = slugify(addressLabel);
        const n = String((idx ?? 0) + 1).padStart(2, "0");
        const suffix = variant === "original" ? "origineel" : "SocialFactoryAI";
        return `${addr ? addr + " - " : ""}foto ${n} - ${suffix}.jpg`;
    };

    // AI-edit panel state — only one job runs at a time within this lightbox
    const [aiState, setAiState] = useState({
        commentId: null,     // which comment triggered the panel (null = direct admin edit)
        directMode: false,   // true when admin starts AI without a source comment
        directText: "",      // admin's own instruction when in direct mode
        extraText: "",
        job: null,           // the latest job doc once submitted
        polling: false,
        elapsed: 0,
    });

    // AI photo presets — Dutch prompt templates loaded once. Kept separate
    // from aiState so the many full-object resets above don't wipe the list.
    const [aiPresets, setAiPresets] = useState([]);
    const [aiPresetKey, setAiPresetKey] = useState("");

    // Reel (9:16) generation state. `job` holds the currently-polling reel
    // job; `view` toggles between original landscape and the reel variant in
    // the main image area. Persisted-completed reels live in `reelUrls`
    // prop (passed from parent's request doc) — this state only tracks the
    // in-flight job and the display preference.
    const [reelState, setReelState] = useState({ job: null, elapsed: 0, view: "original" });

    // After a regular video shot completes we offer to auto-generate the
    // matching 9:16 reel version using the same prompt + the photo's reel
    // counterparts. Tracked in a Set so each completed job only triggers the
    // modal once per session.
    const [reelVideoOffer, setReelVideoOffer] = useState({ open: false, sourceJob: null, start: null, end: null });
    const [reelOfferedJobIds, setReelOfferedJobIds] = useState(() => new Set());

    // Kling V3 Pro image-to-video state — independent from the photo-edit AI.
    // `presets` is loaded once when the lightbox mounts.
    const [videoState, setVideoState] = useState({
        open: false,            // panel is expanded
        presets: [],            // [{key,label_nl,category,requires_end_image}, ...]
        presetKey: "",          // selected dropdown value
        extraText: "",          // optional Dutch hint appended to the Kling prompt
        startImageUrl: "",      // override start (used by transitions from slider, also user-picked)
        endImageUrl: "",        // chosen end image (for transition presets OR optional morph)
        showStartPicker: false, // toggles the start thumbnail grid
        showEndPicker: false,   // toggles the end thumbnail grid
        job: null,              // active job doc once submitted
        elapsed: 0,             // seconds since submit
        history: [],            // completed/failed jobs for this photo
    });

    useEffect(() => {
        setIdx(openIndex);
        setShowAnnotations(true);
        // Only fully reset states when the lightbox is being (re)opened from
        // the parent (openIndex changed). Internal arrow navigation does NOT
        // reset state — the load-effect below will auto-resume any in-flight
        // jobs from the backend instead, so admins can run multiple AI edits
        // and video generations concurrently while flipping through photos.
        setAiState({ commentId: null, directMode: false, directText: "", extraText: "", job: null, polling: false, elapsed: 0 });
        setAiPresetKey("");
        setReelState({ job: null, elapsed: 0, view: "original" });
        setVideoState((s) => ({ ...s, open: false, presetKey: "", extraText: "", startImageUrl: "", endImageUrl: "", showStartPicker: false, showEndPicker: false, job: null, elapsed: 0, history: [] }));
    }, [openIndex]);

    // When admin uses arrow keys / chevrons to switch photos WITHOUT closing
    // the lightbox, reset the local visible state so the next photo starts
    // fresh — but the loading detection below will auto-resume any active
    // backend jobs.
    const prevIdxRef = useRef(openIndex);
    useEffect(() => {
        if (idx === null) return;
        if (prevIdxRef.current === idx) return;
        const wasGenerating = (videoState.job?.status === "pending" || videoState.job?.status === "processing")
            || (aiState.job?.status === "pending" || aiState.job?.status === "processing");
        if (wasGenerating) {
            toast.info(`Foto #${prevIdxRef.current + 1} blijft in de achtergrond genereren`, { duration: 3500 });
        }
        prevIdxRef.current = idx;
        // Reset transient state but keep cached presets list
        setAiState({ commentId: null, directMode: false, directText: "", extraText: "", job: null, polling: false, elapsed: 0 });
        setAiPresetKey("");
        setReelState({ job: null, elapsed: 0, view: "original" });
        setVideoState((s) => ({
            ...s,
            open: false,
            presetKey: "",
            extraText: "",
            startImageUrl: "",
            endImageUrl: "",
            showStartPicker: false,
            showEndPicker: false,
            job: null,
            elapsed: 0,
            history: [],
        }));
    }, [idx]);  // eslint-disable-line react-hooks/exhaustive-deps

    // Polling effect that fetches in-flight job counts every 6s.
    // Drives the small "N jobs running" badge in the top bar so admins know
    // backgrounded generations are still progressing while they flip photos.
    const [activeJobs, setActiveJobs] = useState({ video: 0, ai: 0, ready: 0 });
    const knownCompletedRef = useRef(new Set());
    useEffect(() => {
        if (!requestId) return;
        let cancelled = false;
        const tick = async () => {
            try {
                const [v, a] = await Promise.all([
                    api.get(`/requests/${requestId}/video-shots`),
                    api.get(`/requests/${requestId}/ai-edit-jobs`).catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                const vActive = (v.data || []).filter((j) => j.status === "pending" || j.status === "processing");
                const aActive = (a.data || []).filter((j) => j.status === "pending" || j.status === "processing");
                // Detect newly-finished jobs (not in our `known` set yet) so we
                // can toast about them — but skip the very first poll to avoid
                // spamming about old completed jobs.
                const initial = knownCompletedRef.current.size === 0 && (v.data?.length || 0) + (a.data?.length || 0) === 0;
                let newlyDone = 0;
                for (const j of [...(v.data || []), ...(a.data || [])]) {
                    if (j.status === "completed" && !knownCompletedRef.current.has(j.id)) {
                        if (!initial && knownCompletedRef.current.size > 0) {
                            // Only toast if we've seen jobs before — initial load shouldn't spam
                            const photoNum = (j.photo_index ?? 0) + 1;
                            const kind = j.preset_key ? "Video" : "AI-foto";
                            toast.success(`${kind} voor foto #${photoNum} is klaar`);
                            newlyDone += 1;
                        }
                        knownCompletedRef.current.add(j.id);
                    }
                }
                setActiveJobs((prev) => ({
                    video: vActive.length,
                    ai: aActive.length,
                    ready: prev.ready + newlyDone,
                }));
            } catch { /* non-blocking */ }
        };
        tick();
        const id = setInterval(tick, 6000);
        return () => { cancelled = true; clearInterval(id); };
    }, [requestId]);

    // Load shot presets once + auto-resume any pending/processing video-shot
    // or AI-edit job for this photo so the admin can navigate away while jobs
    // keep running in the background.
    useEffect(() => {
        if (idx === null || idx === undefined || !requestId) return;
        let cancelled = false;
        (async () => {
            try {
                if (videoState.presets.length === 0) {
                    const { data } = await api.get("/video-shots/presets");
                    if (cancelled) return;
                    setVideoState((s) => ({ ...s, presets: data || [] }));
                }
                if (aiPresets.length === 0) {
                    try {
                        const { data } = await api.get("/ai-photo-presets");
                        if (!cancelled) setAiPresets(data || []);
                    } catch { /* non-blocking */ }
                }
                const [vHistRes, aiActiveRes, reelRes] = await Promise.all([
                    api.get(`/requests/${requestId}/video-shots`),
                    api.get(`/requests/${requestId}/ai-edit-jobs`).catch(() => ({ data: [] })),
                    api.get(`/requests/${requestId}/reel-jobs`).catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                const photoHistory = (vHistRes.data || []).filter((j) => j.photo_index === idx);
                // Find an in-flight video-shot for this photo (pending/processing)
                const activeVideo = photoHistory.find((j) => j.status === "pending" || j.status === "processing");
                setVideoState((s) => ({
                    ...s,
                    history: photoHistory,
                    // Only override the job if we don't already have a local one matching this idx
                    job: s.job && (s.job.status === "pending" || s.job.status === "processing") ? s.job : (activeVideo || s.job),
                    open: s.open || !!activeVideo,
                }));
                // Find an in-flight AI-edit job for this photo
                const activeAi = (aiActiveRes.data || []).find(
                    (j) => j.photo_index === idx && (j.status === "pending" || j.status === "processing"),
                );
                if (activeAi) {
                    setAiState((s) => ({
                        ...s,
                        job: s.job && (s.job.status === "pending" || s.job.status === "processing") ? s.job : activeAi,
                        directMode: !activeAi.comment_id,
                        commentId: activeAi.comment_id || null,
                    }));
                }
                // Build AI-edit history for THIS photo (only completed)
                const photoAiHistory = (aiActiveRes.data || [])
                    .filter((j) => j.photo_index === idx && j.status === "completed" && j.result_url);
                setAiHistory(photoAiHistory);

                // Resume any in-flight reel job for this photo so the spinner
                // doesn't disappear on photo navigation.
                const activeReel = (reelRes.data || []).find(
                    (j) => j.photo_index === idx && (j.status === "pending" || j.status === "processing"),
                );
                if (activeReel) {
                    setReelState((s) => ({ ...s, job: activeReel }));
                }
            } catch {
                /* non-blocking */
            }
        })();
        return () => { cancelled = true; };
    }, [requestId, idx]);

    // Poll the active video-shot job while it's running
    useEffect(() => {
        const j = videoState.job;
        if (!j) return;
        if (j.status !== "pending" && j.status !== "processing") return;
        let cancelled = false;
        const tick = async () => {
            try {
                const { data } = await api.get(`/video-shots/${j.id}`);
                if (cancelled) return;
                setVideoState((s) => ({ ...s, job: data, elapsed: s.elapsed + 4 }));
                if (data.status === "completed") {
                    toast.success("Videoshot klaar — bekijk het resultaat");
                    // Refresh history so the new job lands in the list
                    try {
                        const { data: history } = await api.get(`/requests/${requestId}/video-shots`);
                        if (!cancelled) {
                            const photoHistory = (history || []).filter((x) => x.photo_index === idx);
                            setVideoState((s) => ({ ...s, history: photoHistory }));
                        }
                    } catch { /* ignore */ }
                } else if (data.status === "failed") {
                    toast.error(`Videoshot mislukt: ${data.error || "onbekende fout"}`);
                }
            } catch {
                /* ignore transient errors */
            }
        };
        const id = setInterval(tick, 4000);
        return () => { cancelled = true; clearInterval(id); };
    }, [videoState.job?.id, videoState.job?.status, requestId, idx]);

    // Offer to generate the reel (9:16) version once the regular videoshot
    // completes. We only show the modal if BOTH the source photos used by
    // the original job have a matching reel asset (otherwise the user would
    // see a confusing "no reel available" error). The mapping is:
    //   - landscape `photos[i]`            -> `reelUrls[i]`
    //   - landscape `previousUrls[i]`      -> `reelOriginalUrls[i]`
    // For non-transition shots we only need a reel for the source.
    useEffect(() => {
        const j = videoState.job;
        if (!j || j.status !== "completed") return;
        if (reelOfferedJobIds.has(j.id)) return;
        // If this job is itself ALREADY a reel version (source is a reel URL)
        // we don't recursively offer another reel.
        const allReelUrls = new Set([
            ...Object.values(reelUrls || {}),
            ...Object.values(reelOriginalUrls || {}),
        ]);
        const sourceUrl = j.source_url || photos[idx];
        if (allReelUrls.has(sourceUrl)) {
            setReelOfferedJobIds((s) => { const n = new Set(s); n.add(j.id); return n; });
            return;
        }
        const findReel = (url) => {
            if (!url) return null;
            const i = (photos || []).findIndex((p) => p === url);
            if (i >= 0) return (reelUrls || {})[String(i)] || null;
            for (const [k, v] of Object.entries(previousUrls || {})) {
                if (v === url) return (reelOriginalUrls || {})[String(k)] || null;
            }
            return null;
        };
        const reelStart = findReel(sourceUrl);
        const reelEnd = j.end_image_url ? findReel(j.end_image_url) : null;
        // Offer only if we have a reel for the start, and (for transitions)
        // for the end too. Missing reels mean admin has to generate them first.
        const ok = !!reelStart && (!j.end_image_url || !!reelEnd);
        if (ok) {
            setReelVideoOffer({ open: true, sourceJob: j, start: reelStart, end: reelEnd });
        }
        setReelOfferedJobIds((s) => { const n = new Set(s); n.add(j.id); return n; });
    }, [videoState.job?.id, videoState.job?.status, photos, idx, previousUrls, reelUrls, reelOriginalUrls, reelOfferedJobIds]);

    const declineReelOffer = () => {
        setReelVideoOffer({ open: false, sourceJob: null, start: null, end: null });
    };

    const acceptReelOffer = async () => {
        const j = reelVideoOffer.sourceJob;
        if (!j) { declineReelOffer(); return; }
        try {
            const { data } = await api.post(
                `/requests/${requestId}/ai-photos/${idx}/video-shot`,
                {
                    // Re-use exactly the same prompt as the original job. For
                    // custom prompts (`preset_key=="custom"`) we send the saved
                    // text as `custom_prompt`; for preset jobs we resubmit the
                    // preset key so the backend resolves the latest preset.
                    preset_key: j.preset_key === "custom" ? null : j.preset_key,
                    custom_prompt: j.preset_key === "custom" ? (j.custom_prompt || j.optimized_prompt || "") : null,
                    admin_extra_nl: j.admin_extra_nl || "",
                    start_image_url: reelVideoOffer.start,
                    end_image_url: reelVideoOffer.end || null,
                },
            );
            // Replace the current job with the new reel job so polling picks
            // it up. The previous (completed) job is still in `history`.
            setVideoState((s) => ({ ...s, job: data, elapsed: 0, open: true }));
            toast.success("Reel-videoshot gestart met dezelfde prompt");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reel-versie starten mislukt");
        }
        declineReelOffer();
    };

    // Poll the AI job while it's running
    useEffect(() => {
        const j = aiState.job;
        if (!j) return;
        if (j.status !== "pending" && j.status !== "processing") return;
        let cancelled = false;
        const tick = async () => {
            try {
                const { data } = await api.get(`/ai-edit-jobs/${j.id}`);
                if (cancelled) return;
                setAiState((s) => ({ ...s, job: data, elapsed: s.elapsed + 3 }));
                if (data.status === "completed") {
                    toast.success("AI-bewerking klaar — bekijk het resultaat");
                } else if (data.status === "failed") {
                    toast.error(`AI mislukt: ${data.error || "onbekende fout"}`);
                }
            } catch {
                /* ignore transient errors */
            }
        };
        const id = setInterval(tick, 3000);
        return () => { cancelled = true; clearInterval(id); };
    }, [aiState.job?.id, aiState.job?.status]);

    // Poll the reel job while it's running. Refresh parent on completion so
    // `reelUrls[idx]` populates and the View toggle appears.
    useEffect(() => {
        const j = reelState.job;
        if (!j) return;
        if (j.status !== "pending" && j.status !== "processing") return;
        let cancelled = false;
        const tick = async () => {
            try {
                const { data } = await api.get(`/reel-jobs/${j.id}`);
                if (cancelled) return;
                setReelState((s) => ({ ...s, job: data, elapsed: s.elapsed + 3 }));
                if (data.status === "completed") {
                    toast.success("Reel-versie (9:16) klaar");
                    onReplaced?.(); // ask parent to reload, picks up reel_photo_urls
                    setReelState((s) => ({ ...s, view: "reel" }));
                } else if (data.status === "failed") {
                    toast.error(`Reel mislukt: ${data.error || "onbekende fout"}`);
                } else if (data.status === "cancelled") {
                    toast.message("Reel geannuleerd");
                }
            } catch {
                /* ignore transient errors */
            }
        };
        const id = setInterval(tick, 3000);
        return () => { cancelled = true; clearInterval(id); };
    }, [reelState.job?.id, reelState.job?.status, onReplaced]);

    useEffect(() => {
        if (idx === null || idx === undefined) return;
        const handler = (e) => {
            const tag = (e.target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea") return;
            if (e.key === "Escape") onClose?.();
            else if (e.key === "ArrowLeft") setIdx((i) => (i === null ? null : (i - 1 + photos.length) % photos.length));
            else if (e.key === "ArrowRight") setIdx((i) => (i === null ? null : (i + 1) % photos.length));
        };
        window.addEventListener("keydown", handler);
        const release = lockBodyScroll(true);
        return () => {
            window.removeEventListener("keydown", handler);
            release();
        };
    }, [idx, photos.length, onClose]);

    // AI-edit history for THIS photo (all completed jobs, oldest → newest)
    const [aiHistory, setAiHistory] = useState([]);

    // Compare toggle — shown when this photo has a pre-AI version. Hooks must
    // be declared BEFORE the early `idx === null` returns below.
    const [showCompare, setShowCompare] = useState(false);
    useEffect(() => { setShowCompare(false); }, [idx]);

    if (idx === null || idx === undefined || !photos || photos.length === 0) return null;
    if (idx < 0 || idx >= photos.length) return null;

    // currentUrl follows the reel-view toggle: when the admin has clicked
    // "Bekijk reel", the displayed photo (and download / replace targets)
    // become the 9:16 variant. Falls back gracefully if reel hasn't been
    // generated yet.
    const reelUrlForIdx = reelUrls && reelUrls[String(idx)];
    const showingReel = reelState.view === "reel" && !!reelUrlForIdx;
    const currentUrl = showingReel ? reelUrlForIdx : photos[idx];
    const photoComments = commentsByPhoto[idx] || [];
    const aiOpen = aiState.commentId !== null;
    const job = aiState.job;
    const showSlider = job && job.status === "completed" && job.result_url;
    const aiBusy = job && (job.status === "pending" || job.status === "processing");

    // When an AI-edit was approved historically the original lives in
    // `previousUrls[idx]`. Admin can toggle a Before/After slider on any
    // AI-edited photo even when no new edit was just made.
    const historicalOriginal = previousUrls && previousUrls[String(idx)] ? previousUrls[String(idx)] : null;
    const isAiEdited = !!historicalOriginal;
    // When the lightbox is in reel-view AND a reel-original exists, prefer the
    // 9:16 pair for the compare slider so admin actually compares the two
    // reel variants (which is what they switched view for). Falls back to the
    // landscape pair gracefully when no reel-original was generated yet.
    const compareBeforeUrl = (showingReel && reelOriginalUrls?.[String(idx)])
        ? reelOriginalUrls[String(idx)]
        : historicalOriginal;

    const triggerUpload = () => {
        if (uploading) return;
        fileInputRef.current?.click();
    };

    // Upload a file directly to R2 (with browser progress) and tell the
    // backend to swap `ai_photo_urls[idx]`. Used by both the file picker and
    // the drag-and-drop zone below.
    const uploadAndReplace = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Selecteer een afbeelding (jpg, png, webp...)");
            return;
        }
        setUploading(true);
        setUploadProgress(0);
        try {
            // 1. Get a presigned PUT URL on R2 so the bytes never cross our backend.
            const init = await api.post("/upload/direct/init", {
                filename: file.name,
                content_type: file.type || "image/jpeg",
            });
            const { upload_url, content_type, final_url } = init.data || {};
            if (!upload_url || !final_url) throw new Error("Direct-upload init mislukte");

            // 2. PUT to R2 via XHR so we can show a progress bar (fetch has no upload progress).
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open("PUT", upload_url);
                xhr.setRequestHeader("Content-Type", content_type || file.type || "image/jpeg");
                xhr.upload.addEventListener("progress", (e) => {
                    if (e.lengthComputable) {
                        setUploadProgress(Math.round((e.loaded / e.total) * 100));
                    }
                });
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) resolve(null);
                    else reject(new Error(`R2-upload mislukte (HTTP ${xhr.status})`));
                };
                xhr.onerror = () => reject(new Error("Netwerkfout tijdens upload"));
                xhr.send(file);
            });

            // 3. Tell backend to swap this index + delete related comments
            const { data } = await api.post(
                `/requests/${requestId}/ai-photos/${idx}/replace`,
                { new_photo_url: final_url },
            );
            toast.success(
                data.comments_deleted > 0
                    ? `Foto vervangen — ${data.comments_deleted} opmerking${data.comments_deleted === 1 ? "" : "en"} verwijderd`
                    : "Foto vervangen",
            );
            onReplaced?.(final_url, idx);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Vervangen mislukt");
        }
        setUploading(false);
        setUploadProgress(0);
    };

    const handleFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        uploadAndReplace(file);
    };

    // --- Drag & drop handlers ---
    const onDragOver = (e) => {
        // Only react when an actual file is being dragged
        if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
        e.preventDefault();
        e.stopPropagation();
        if (!uploading) setDropActive(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only deactivate when the cursor truly leaves the lightbox (not a child element)
        if (e.target === e.currentTarget) setDropActive(false);
    };
    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDropActive(false);
        if (uploading) return;
        const file = e.dataTransfer?.files?.[0];
        if (file) uploadAndReplace(file);
    };

    // --- AI edit handlers ---
    const openAiPanel = (commentId) => {
        // If the same comment is clicked twice, toggle the panel closed.
        if (aiState.commentId === commentId) {
            setAiState({ commentId: null, directMode: false, directText: "", extraText: "", job: null, polling: false, elapsed: 0 });
            return;
        }
        setAiState({ commentId, directMode: false, directText: "", extraText: "", job: null, polling: false, elapsed: 0 });
    };

    const openDirectAiPanel = () => {
        // Toggle the always-on AI panel above the comments. Resets any in-flight job.
        if (aiState.directMode) {
            setAiState({ commentId: null, directMode: false, directText: "", extraText: "", job: null, polling: false, elapsed: 0 });
            setAiPresetKey("");
            return;
        }
        setAiState({ commentId: null, directMode: true, directText: "", extraText: "", job: null, polling: false, elapsed: 0 });
        setAiPresetKey("");
    };

    const startAiEdit = async () => {
        if (aiState.directMode) {
            if (!aiState.directText.trim()) {
                toast.error("Schrijf eerst een instructie");
                return;
            }
            try {
                const { data } = await api.post(`/requests/${requestId}/ai-photos/${idx}/ai-edit`, {
                    user_prompt_nl: aiState.directText.trim(),
                    admin_extra_prompt: aiState.extraText || "",
                });
                setAiState((s) => ({ ...s, job: data, elapsed: 0 }));
                toast.success("AI gestart — duurt typisch 30–90 seconden");
            } catch (err) {
                toast.error(err.response?.data?.detail || "Starten mislukt");
            }
            return;
        }
        if (!aiState.commentId) return;
        try {
            const { data } = await api.post(`/comments/${aiState.commentId}/ai-edit`, {
                admin_extra_prompt: aiState.extraText || "",
            });
            setAiState((s) => ({ ...s, job: data, elapsed: 0 }));
            toast.success("AI gestart — duurt typisch 30–90 seconden");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Starten mislukt");
        }
    };

    const approveAiEdit = async () => {
        if (!job?.id) return;
        try {
            await api.post(`/ai-edit-jobs/${job.id}/approve`);
            toast.success("AI-bewerking goedgekeurd — foto vervangen");
            setAiState({ commentId: null, directMode: false, directText: "", extraText: "", job: null, polling: false, elapsed: 0 });
            onReplaced?.(job.result_url, idx);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Goedkeuren mislukt");
        }
    };

    const rejectAiEdit = async () => {
        if (!job?.id) return;
        try {
            await api.post(`/ai-edit-jobs/${job.id}/reject`);
            toast.info("AI-bewerking verworpen");
            setAiState((s) => ({ ...s, job: { ...s.job, status: "rejected" } }));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Verwerpen mislukt");
        }
    };

    const retryAiEdit = async () => {
        if (!aiState.commentId) return;
        setAiState((s) => ({ ...s, job: null, elapsed: 0 }));
        // Slight delay so the panel UI resets cleanly
        setTimeout(() => startAiEdit(), 50);
    };

    // --- Kling video shot handlers ---
    const toggleVideoPanel = () => {
        setVideoState((s) => ({ ...s, open: !s.open }));
    };

    // --- Reel (9:16) handlers ---
    const reelExists = !!(reelUrls && reelUrls[String(idx)]);
    const reelOriginalExists = !!(reelOriginalUrls && reelOriginalUrls[String(idx)]);
    const hasOriginal = !!(previousUrls && previousUrls[String(idx)]);
    const reelRunning = !!(reelState.job && (reelState.job.status === "pending" || reelState.job.status === "processing"));

    const startReel = async (variant = "current") => {
        if (reelRunning) return;
        if (idx === null || idx === undefined) return;
        // Portrait check only relevant when generating from the CURRENT photo
        if (variant === "current") {
            const probe = async () => new Promise((resolve) => {
                const im = new Image();
                im.onload = () => resolve({ w: im.naturalWidth, h: im.naturalHeight });
                im.onerror = () => resolve({ w: 0, h: 0 });
                im.src = `${BACKEND}${photos[idx]}`;
            });
            const { w, h } = await probe();
            if (h > 0 && w > 0 && h >= w) {
                const ok = window.confirm(
                    "Deze foto is al portret-formaat (verticaal). Een reel-versie zal er weinig anders uitzien.\n\nToch doorgaan? (kost €0.30)"
                );
                if (!ok) return;
            }
        }
        try {
            const { data } = await api.post(
                `/requests/${requestId}/ai-photos/${idx}/reel`,
                null,
                { params: { variant } },
            );
            setReelState({ job: data, elapsed: 0, view: "original" });
            toast.success(variant === "original" ? "Reel van origineel gestart — ±60s" : "Reel-generatie gestart — ±60s");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Kon reel niet starten");
        }
    };

    const cancelReel = async () => {
        const j = reelState.job;
        if (!j) return;
        try {
            await api.post(`/reel-jobs/${j.id}/cancel`);
            setReelState({ job: null, elapsed: 0, view: "original" });
            toast.message("Reel-job geannuleerd");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Annuleren mislukt");
        }
    };

    const deleteReel = async () => {
        if (!reelExists) return;
        if (!window.confirm("Reel-versie (9:16) verwijderen? De originele foto blijft behouden.")) return;
        try {
            await api.delete(`/requests/${requestId}/ai-photos/${idx}/reel`);
            setReelState({ job: null, elapsed: 0, view: "original" });
            onReplaced?.();
            toast.success("Reel verwijderd");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Verwijderen mislukt");
        }
    };

    const startVideoShot = async () => {
        const isCustom = videoState.presetKey === "__custom__";
        if (!videoState.presetKey) {
            toast.error("Kies een camera-beweging of 'Eigen prompt'");
            return;
        }
        if (isCustom && !(videoState.extraText || "").trim()) {
            toast.error("Typ je eigen prompt om te genereren");
            return;
        }
        const preset = videoState.presets.find((p) => p.key === videoState.presetKey);
        if (preset?.requires_end_image && !videoState.endImageUrl) {
            toast.error("Deze transition heeft een tweede (eind-)foto nodig");
            return;
        }
        try {
            const { data } = await api.post(
                `/requests/${requestId}/ai-photos/${idx}/video-shot`,
                {
                    // In custom mode preset_key is omitted server-side; we send
                    // null so the backend takes the `custom_prompt` branch.
                    preset_key: isCustom ? null : videoState.presetKey,
                    custom_prompt: isCustom ? (videoState.extraText || "").trim() : null,
                    admin_extra_nl: isCustom ? "" : (videoState.extraText || ""),
                    start_image_url: videoState.startImageUrl || null,
                    end_image_url: videoState.endImageUrl || null,
                },
            );
            setVideoState((s) => ({ ...s, job: data, elapsed: 0 }));
            toast.success("Kling V3 Pro gestart — duurt 2–5 minuten");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Starten mislukt");
        }
    };

    // Open the video panel pre-configured to fade FROM the pre-AI version
    // TO the current (or just-generated) AI version. Default preset is the
    // "Voor → Na AI-bewerking" transition; admin can still change it.
    const openTransitionFromSlider = () => {
        // Pick the right "before" and "after" URLs depending on whether the
        // admin is looking at a just-finished job (slider showing) or a
        // historical AI-edit (previousUrls map).
        // When the lightbox is in "reel view" we prefer the 9:16 reel pair so
        // the resulting transition video is also 9:16 — that's almost always
        // what the admin wants when they switched to reel view.
        let beforeUrl = null;
        let afterUrl = null;
        if (showSlider && job?.source_url && job?.result_url) {
            beforeUrl = job.source_url;
            afterUrl = job.result_url;
        } else if (previousUrls && previousUrls[String(idx)]) {
            const reelOrig = reelOriginalUrls?.[String(idx)];
            const reelCur = reelUrls?.[String(idx)];
            if (showingReel && reelOrig && reelCur) {
                beforeUrl = reelOrig;
                afterUrl = reelCur;
            } else {
                beforeUrl = previousUrls[String(idx)];
                afterUrl = currentUrl;
            }
        }
        if (!beforeUrl || !afterUrl) {
            toast.error("Geen voor/na-versie gevonden voor deze foto");
            return;
        }
        setVideoState((s) => ({
            ...s,
            open: true,
            presetKey: "transition_before_after_ai",
            startImageUrl: beforeUrl,
            endImageUrl: afterUrl,
            extraText: "",
            job: null,
            elapsed: 0,
        }));
        // Scroll the panel into view after the next paint
        setTimeout(() => {
            const el = document.querySelector('[data-testid="video-shot-panel"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    const recoverVideoShot = async (jobId) => {
        try {
            const { data } = await api.post(`/video-shots/${jobId}/recover`);
            setVideoState((s) => ({ ...s, job: data }));
            toast.success("Video opgehaald");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Ophalen mislukt");
        }
    };

    const cancelVideoShot = async (jobId) => {
        if (!window.confirm("Deze generatie annuleren?\n\nFal Kling blijft eventueel op de achtergrond doorlopen, maar je kan meteen een nieuwe video genereren.")) return;
        try {
            await api.post(`/video-shots/${jobId}/cancel`);
            // Reset to a fresh form so admin can immediately start a new generation
            setVideoState((s) => ({
                ...s,
                job: null,
                elapsed: 0,
                presetKey: s.presetKey,
                extraText: s.extraText,
            }));
            toast.info("Generatie geannuleerd — je kan een nieuwe starten");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Annuleren mislukt");
        }
    };

    // Manual fallbacks for when Fal succeeded but our automatic download failed
    const attachVideoUrl = async (jobId) => {
        const url = window.prompt(
            "Plak de directe MP4-URL van Fal.ai (rechtsklik op de video in je fal.ai dashboard → 'Link kopiëren').\n\nMoet beginnen met https:// en .mp4 bevatten.",
            "",
        );
        if (!url || !url.trim()) return;
        try {
            const { data } = await api.post(`/video-shots/${jobId}/attach-url`, { fal_url: url.trim() });
            setVideoState((s) => ({ ...s, job: data }));
            toast.success("Video gekoppeld via URL");
        } catch (err) {
            toast.error(err.response?.data?.detail || "URL koppelen mislukt");
        }
    };

    const manualUploadVideo = async (jobId, file) => {
        if (!file) return;
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post(`/video-shots/${jobId}/manual-upload`, form, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 600000,
            });
            setVideoState((s) => ({ ...s, job: data }));
            toast.success("Video handmatig geüpload");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload mislukt");
        }
    };

    const deleteVideoShot = async (jobId) => {
        if (!window.confirm("Deze videoshot definitief verwijderen?")) return;
        try {
            await api.delete(`/video-shots/${jobId}`);
            setVideoState((s) => ({
                ...s,
                job: s.job?.id === jobId ? null : s.job,
                history: s.history.filter((x) => x.id !== jobId),
            }));
            toast.info("Videoshot verwijderd");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Verwijderen mislukt");
        }
    };

    const retryVideoShot = () => {
        setVideoState((s) => ({ ...s, job: null, elapsed: 0 }));
        setTimeout(() => startVideoShot(), 50);
    };

    const videoBusy = videoState.job && (videoState.job.status === "pending" || videoState.job.status === "processing");
    const videoResult = videoState.job && videoState.job.status === "completed" ? videoState.job : null;

    const videoDownloadName = () => {
        const addr = slugify(addressLabel);
        const n = String((idx ?? 0) + 1).padStart(2, "0");
        const preset = (videoState.presetKey || videoState.job?.preset_key || "shot").replace(/_/g, "-");
        return `${addr ? addr + " - " : ""}foto ${n} - ${preset} - SocialFactoryAI.mp4`;
    };

    return (
        <div
            className="fixed inset-0 z-[200] bg-black/95 flex flex-col items-center justify-start overflow-y-auto"
            onClick={(e) => {
                // Only close when the click is on the backdrop itself, not
                // bubbled from a child (button, file input, etc.). This is
                // the standard modal pattern and is bullet-proof against
                // programmatic clicks on the hidden file input.
                if (e.target === e.currentTarget) onClose();
            }}
            onDragEnter={onDragOver}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            data-testid="admin-ai-photo-lightbox"
        >
            {/* Hidden file input — stopPropagation on its click so the
                programmatic click from triggerUpload doesn't bubble up to
                the lightbox's onClose handler. */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFile}
                onClick={(e) => e.stopPropagation()}
                data-testid="admin-replace-photo-file"
            />

            {/* Drag-and-drop overlay — appears when a file is being dragged over the lightbox */}
            {dropActive && (
                <div
                    className="fixed inset-0 z-[210] bg-[#E5A93D]/15 backdrop-blur-sm border-4 border-dashed border-[#E5A93D] flex flex-col items-center justify-center text-zinc-950 pointer-events-none"
                    data-testid="admin-photo-drop-overlay"
                >
                    <Upload className="h-16 w-16 text-[#E5A93D] mb-4 animate-bounce" />
                    <div className="font-display font-black text-3xl tracking-tight mb-2">Laat de foto los</div>
                    <div className="text-sm text-[#E5A93D]">Vervangt foto #{idx + 1} in dit project</div>
                </div>
            )}

            {/* Upload progress bar — bottom of screen during direct R2 upload */}
            {uploading && (
                <div
                    className="fixed bottom-0 inset-x-0 z-[215] bg-[#FFFFFF]/95 backdrop-blur-sm border-t border-[#E5A93D]/40 px-6 py-3 flex items-center gap-4"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="admin-photo-upload-progress"
                >
                    <Loader2 className="h-5 w-5 text-[#E5A93D] animate-spin flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-display font-bold text-zinc-950 mb-1">
                            Foto uploaden naar Cloudflare R2…
                        </div>
                        <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-[#E5A93D] transition-all duration-150"
                                style={{ width: `${uploadProgress}%` }}
                            />
                        </div>
                    </div>
                    <span className="text-xs font-mono text-[#E5A93D] flex-shrink-0">{uploadProgress}%</span>
                </div>
            )}

            {/* Top bar */}
            <div className="sticky top-0 inset-x-0 flex items-center justify-between p-4 lg:p-6 z-30 w-full pointer-events-none">
                <div className="text-xs font-mono text-zinc-400 bg-black/60 backdrop-blur-sm px-3 h-9 inline-flex items-center rounded-sm border border-white/10 pointer-events-auto">
                    {idx + 1} / {photos.length}
                    {photoComments.length > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[#E5A93D]">
                            <MessageCircle className="h-3 w-3" /> {photoComments.length}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 pointer-events-auto">
                    {/* AI history — only when there's at least 1 AI-edit job for this photo */}
                    {aiHistory.length > 0 && onOpenAiHistory && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onOpenAiHistory(idx, aiHistory); }}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-[#E5A93D]/15 hover:bg-[#E5A93D]/25 text-[#E5A93D] rounded-sm transition backdrop-blur-sm border border-[#E5A93D]/30"
                            data-testid="admin-photo-ai-history-btn"
                            title={`Open ${aiHistory.length} AI-versie${aiHistory.length === 1 ? "" : "s"} voor deze foto`}
                        >
                            <Sparkles className="h-3.5 w-3.5" /> History ({aiHistory.length})
                        </button>
                    )}
                    {/* Compare with original (Before/After slider) — only when this
                        photo has a saved pre-AI version */}
                    {isAiEdited && !showSlider && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setShowCompare((v) => !v); }}
                            className={`inline-flex items-center gap-1.5 text-xs px-3 h-9 rounded-sm transition active:scale-95 backdrop-blur-sm border ${showCompare ? "bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold border-[#E5A93D]" : "bg-zinc-100 hover:bg-zinc-200 text-black border-zinc-200"}`}
                            data-testid="admin-photo-compare-btn"
                            title="Vergelijk de huidige (AI) versie met het origineel"
                        >
                            <GitCompareArrows className="h-3.5 w-3.5" />
                            {showCompare ? "Sluit vergelijking" : "Vergelijk met origineel"}
                        </button>
                    )}
                    {/* Download original (only when an original exists) */}
                    {isAiEdited && (
                        <a
                            href={`${BACKEND}${historicalOriginal}?dl=${encodeURIComponent(downloadName("original"))}`}
                            download={downloadName("original")}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition backdrop-blur-sm border border-zinc-200"
                            data-testid="admin-photo-download-original-btn"
                            title="Download het origineel"
                        >
                            <Download className="h-3.5 w-3.5" /> Origineel
                        </a>
                    )}
                    {/* Download current (the AI version when AI-edited, otherwise raw) */}
                    <a
                        href={`${BACKEND}${currentUrl}?dl=${encodeURIComponent(downloadName(isAiEdited ? "ai" : "original"))}`}
                        download={downloadName(isAiEdited ? "ai" : "original")}
                        onClick={(e) => e.stopPropagation()}
                        className={`inline-flex items-center gap-1.5 text-xs px-3 h-9 rounded-sm transition backdrop-blur-sm border ${isAiEdited ? "bg-[#E5A93D]/15 hover:bg-[#E5A93D]/25 text-[#E5A93D] border-[#E5A93D]/30" : "bg-zinc-100 hover:bg-zinc-200 text-zinc-950 border-zinc-200"}`}
                        data-testid="admin-photo-download-current-btn"
                        title={isAiEdited ? "Download de AI-bewerkte versie" : "Download deze foto"}
                    >
                        <Download className="h-3.5 w-3.5" /> {isAiEdited ? "AI" : "Foto"}
                    </a>
                    {(activeJobs.video > 0 || activeJobs.ai > 0) && (
                        <span
                            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider bg-sky-500/15 border border-sky-500/40 text-sky-700 rounded-sm px-2 h-9"
                            data-testid="lightbox-active-jobs-badge"
                            title="Generaties die nu in de achtergrond draaien"
                        >
                            <Loader2 className="h-3 w-3 animate-spin" />
                            {activeJobs.ai > 0 && <span>{activeJobs.ai}× AI</span>}
                            {activeJobs.video > 0 && <span>{activeJobs.video}× video</span>}
                        </span>
                    )}
                    {videoState.history.length > 0 && onOpenVideoGallery && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onOpenVideoGallery(idx); }}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-sky-500 hover:bg-sky-400 text-black font-bold rounded-sm transition active:scale-95"
                            data-testid="admin-photo-open-video-gallery-btn"
                            title={`Open ${videoState.history.length} bewaarde video shot${videoState.history.length === 1 ? "" : "s"}`}
                        >
                            <Film className="h-3.5 w-3.5" /> Video&apos;s ({videoState.history.length})
                        </button>
                    )}
                    {previousUrls && previousUrls[String(idx)] && !showSlider && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openTransitionFromSlider(); }}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-sky-500 hover:bg-sky-400 text-black font-bold rounded-sm transition active:scale-95"
                            data-testid="admin-photo-transition-btn"
                            title="Maak een fade-video van de oorspronkelijke → AI-bewerkte versie"
                        >
                            <Film className="h-3.5 w-3.5" /> Transition video
                        </button>
                    )}
                    {/* Reel (9:16) controls — pill on the left, view-toggle on the right when available. */}
                    {reelExists && reelState.view === "original" && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setReelState((s) => ({ ...s, view: "reel" })); }}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-fuchsia-600 hover:bg-fuchsia-500 text-zinc-950 font-bold rounded-sm transition active:scale-95"
                            data-testid="admin-photo-show-reel-btn"
                            title="Bekijk de 9:16 reel-versie"
                        >
                            <Film className="h-3.5 w-3.5" /> Bekijk reel
                        </button>
                    )}
                    {reelExists && reelState.view === "reel" && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setReelState((s) => ({ ...s, view: "original" })); }}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition backdrop-blur-sm border border-zinc-200"
                            data-testid="admin-photo-show-original-btn"
                            title="Terug naar de oorspronkelijke landscape foto"
                        >
                            <RotateCcw className="h-3.5 w-3.5" /> Origineel
                        </button>
                    )}
                    {!reelExists && !reelRunning && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); startReel("current"); }}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-fuchsia-600 hover:bg-fuchsia-500 text-zinc-950 font-bold rounded-sm transition active:scale-95"
                            data-testid="admin-photo-make-reel-btn"
                            title="Genereer een 9:16 portret versie via Nano Banana outpainting (~60s, €0.30)"
                        >
                            <Film className="h-3.5 w-3.5" /> Maak Reel (9:16)
                        </button>
                    )}
                    {hasOriginal && !reelOriginalExists && !reelRunning && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); startReel("original"); }}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-fuchsia-600/70 hover:bg-fuchsia-500/90 text-zinc-950 font-bold rounded-sm transition active:scale-95 border border-fuchsia-400/50"
                            data-testid="admin-photo-make-reel-original-btn"
                            title="Genereer een 9:16 reel-versie van de PRE-AI originele foto (~60s, €0.30)"
                        >
                            <Film className="h-3.5 w-3.5" /> Reel · Origineel
                        </button>
                    )}
                    {reelRunning && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); cancelReel(); }}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-fuchsia-600/70 hover:bg-fuchsia-600/90 text-zinc-950 font-bold rounded-sm transition active:scale-95 animate-pulse"
                            data-testid="admin-photo-reel-running-btn"
                            title="Klik om de reel-generatie te annuleren"
                        >
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reel… {reelState.elapsed}s
                        </button>
                    )}
                    <a
                        href={`${BACKEND}${currentUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition backdrop-blur-sm border border-zinc-200"
                        data-testid="admin-photo-open-tab-btn"
                    >
                        <ExternalLink className="h-3.5 w-3.5" /> Open
                    </a>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); triggerUpload(); }}
                        disabled={uploading}
                        className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="admin-replace-photo-btn"
                    >
                        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                        {uploading ? "Bezig..." : "Vervangen"}
                    </button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
                        className="inline-flex items-center justify-center h-9 w-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition backdrop-blur-sm border border-zinc-200"
                        data-testid="admin-photo-close-btn"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Sticky AI action bar — appears below the top bar whenever a
                completed AI job is on screen. Keeps Goedkeuren / Opnieuw /
                Verwerpen / Downloads safely visible without scrolling. */}
            {showSlider && (
                <div
                    className="sticky top-[68px] inset-x-0 z-30 w-full pointer-events-none px-4 lg:px-6 mb-2"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="ai-result-action-bar"
                >
                    <div className="mx-auto max-w-5xl bg-[#FFFFFF]/95 border border-[#E5A93D]/40 rounded-md backdrop-blur-md shadow-2xl shadow-black/60 px-3 py-2 pointer-events-auto flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#E5A93D] font-bold mr-1">
                            <Sparkles className="h-3.5 w-3.5" /> AI-resultaat klaar
                            {job?.duration_ms && (
                                <span className="text-[10px] font-mono text-zinc-500 ml-1 normal-case">· {(job.duration_ms / 1000).toFixed(1)}s</span>
                            )}
                        </span>
                        <div className="h-5 w-px bg-zinc-200" />
                        <button
                            type="button"
                            onClick={approveAiEdit}
                            className="inline-flex items-center gap-1.5 text-xs px-4 h-9 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-sm transition active:scale-95"
                            data-testid="ai-top-approve-btn"
                            title="Vervangt de foto in het project met het AI-resultaat"
                        >
                            <Check className="h-3.5 w-3.5" /> Goedkeuren
                        </button>
                        <button
                            type="button"
                            onClick={retryAiEdit}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95 border border-zinc-200"
                            data-testid="ai-top-retry-btn"
                        >
                            <RotateCcw className="h-3.5 w-3.5" /> Opnieuw
                        </button>
                        <button
                            type="button"
                            onClick={rejectAiEdit}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-red-500/15 text-red-700 rounded-sm transition active:scale-95 border border-red-500/20"
                            data-testid="ai-top-reject-btn"
                        >
                            <X className="h-3.5 w-3.5" /> Verwerpen
                        </button>
                        <div className="h-5 w-px bg-zinc-200" />
                        <button
                            type="button"
                            onClick={openTransitionFromSlider}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-sky-500 hover:bg-sky-400 text-black font-bold rounded-sm transition active:scale-95"
                            data-testid="ai-top-transition-video-btn"
                            title="Maak een fade-video van de oude → nieuwe versie van deze foto"
                        >
                            <Film className="h-3.5 w-3.5" /> Transition video
                        </button>
                        <div className="h-5 w-px bg-zinc-200" />
                        <a
                            href={`${BACKEND}${job.source_url}`}
                            download={downloadName("original")}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95 backdrop-blur-sm border border-zinc-200"
                            data-testid="ai-download-original-btn"
                            title={`Download originele foto — ${downloadName("original")}`}
                        >
                            <Download className="h-3.5 w-3.5" /> Origineel
                        </a>
                        <a
                            href={`${BACKEND}${job.result_url}`}
                            download={downloadName("ai")}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-95"
                            data-testid="ai-download-result-btn"
                            title={`Download AI-versie (4K JPG) — ${downloadName("ai")}`}
                        >
                            <Download className="h-3.5 w-3.5" /> AI-versie
                        </a>
                    </div>
                </div>
            )}

            {/* Image stage */}
            <div
                className="relative flex items-center justify-center w-full px-2 lg:px-6"
                onClick={(e) => e.stopPropagation()}
            >
                {photos.length > 1 && (
                    <button
                        type="button"
                        onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)}
                        className="absolute left-2 lg:left-6 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-12 w-12 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-full transition active:scale-95 backdrop-blur-sm z-10"
                        data-testid="admin-photo-prev-btn"
                    >
                        <ChevronLeft className="h-6 w-6" />
                    </button>
                )}

                <div className="relative inline-block">
                    {showSlider ? (
                        <BeforeAfterSlider
                            beforeUrl={`${BACKEND}${job.source_url}`}
                            afterUrl={`${BACKEND}${job.result_url}`}
                            className="max-h-[78vh] max-w-[92vw]"
                            testId="ai-result-slider"
                        />
                    ) : (showCompare && isAiEdited) ? (
                        <BeforeAfterSlider
                            beforeUrl={`${BACKEND}${compareBeforeUrl}`}
                            afterUrl={`${BACKEND}${currentUrl}`}
                            className="max-h-[78vh] max-w-[92vw]"
                            testId="admin-photo-compare-slider"
                        />
                    ) : (
                        <>
                            <img
                                src={`${BACKEND}${currentUrl}`}
                                alt={`Foto ${idx + 1}`}
                                className="max-h-[78vh] max-w-[92vw] object-contain block"
                                data-testid="admin-photo-lightbox-image"
                            />
                            {/* Stack any annotation overlays for this photo */}
                            {showAnnotations &&
                                photoComments
                                    .filter((c) => c.annotation_url)
                                    .map((c) => (
                                        <img
                                            key={c.id}
                                            src={`${BACKEND}${c.annotation_url}`}
                                            alt="Annotatie"
                                            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                                        />
                                    ))}
                        </>
                    )}
                    {aiBusy && (
                        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center text-white">
                            <Loader2 className="h-10 w-10 animate-spin text-[#E5A93D] mb-3" />
                            <div className="font-display font-bold text-lg">AI bezig met bewerking…</div>
                            <div className="text-xs text-zinc-500 mt-1">Claude + Fal.ai Nano Banana Pro · 4K</div>
                            <div className="text-[11px] font-mono text-zinc-500 mt-3">{aiState.elapsed}s · meestal 30–90s</div>
                            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                                {/* Always-available cancel so admin can free the slot when Fal hangs */}
                                <button
                                    type="button"
                                    onClick={async () => {
                                        if (!window.confirm("Deze AI-bewerking annuleren?\n\nFal blijft eventueel op de achtergrond doorlopen, maar je kan meteen opnieuw beginnen.")) return;
                                        try {
                                            await api.post(`/ai-edit-jobs/${job.id}/cancel`);
                                            setAiState((s) => ({ ...s, job: null, elapsed: 0 }));
                                            toast.info("AI-bewerking geannuleerd");
                                        } catch (e) {
                                            toast.error(e.response?.data?.detail || "Annuleren mislukt");
                                        }
                                    }}
                                    className="inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-red-500/15 hover:bg-red-500/25 text-red-700 rounded-sm transition active:scale-95 border border-red-500/30"
                                    data-testid="ai-cancel-btn"
                                    title="Stop deze AI-bewerking zodat je opnieuw kan proberen"
                                >
                                    <X className="h-3.5 w-3.5" /> Annuleer
                                </button>
                                {aiState.elapsed >= 180 && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    const { data } = await api.post(`/ai-edit-jobs/${job.id}/recover`);
                                                    setAiState((s) => ({ ...s, job: data }));
                                                    toast.success("Resultaat opgehaald");
                                                } catch (e) {
                                                    toast.error(e.response?.data?.detail || "Ophalen mislukt");
                                                }
                                            }}
                                            className="inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-95"
                                            data-testid="ai-recover-btn"
                                        >
                                            Ophaal van Fal
                                        </button>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                const url = window.prompt(
                                                    "Plak de directe afbeelding-URL van Fal.ai (rechtsklik in fal.ai dashboard → 'Link kopiëren').\n\nMoet https:// en .jpg/.png/.webp bevatten.",
                                                    "",
                                                );
                                                if (!url || !url.trim()) return;
                                                try {
                                                    const { data } = await api.post(`/ai-edit-jobs/${job.id}/attach-url`, { fal_url: url.trim() });
                                                    setAiState((s) => ({ ...s, job: data }));
                                                    toast.success("Afbeelding gekoppeld");
                                                } catch (e) {
                                                    toast.error(e.response?.data?.detail || "Koppelen mislukt");
                                                }
                                            }}
                                            className="inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded-sm transition active:scale-95 border border-zinc-200"
                                            data-testid="ai-attach-url-btn"
                                        >
                                            Plak Fal URL
                                        </button>
                                        <label className="inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded-sm transition active:scale-95 border border-zinc-200 cursor-pointer">
                                            <Upload className="h-3.5 w-3.5" /> Upload afbeelding
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                                                className="hidden"
                                                onChange={async (e) => {
                                                    const f = e.target.files?.[0];
                                                    e.target.value = "";
                                                    if (!f) return;
                                                    try {
                                                        const form = new FormData();
                                                        form.append("file", f);
                                                        const { data } = await api.post(`/ai-edit-jobs/${job.id}/manual-upload`, form, {
                                                            headers: { "Content-Type": "multipart/form-data" },
                                                            timeout: 600000,
                                                        });
                                                        setAiState((s) => ({ ...s, job: data }));
                                                        toast.success("Afbeelding geüpload");
                                                    } catch (err) {
                                                        toast.error(err.response?.data?.detail || "Upload mislukt");
                                                    }
                                                }}
                                                data-testid="ai-manual-upload-input"
                                            />
                                        </label>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {photos.length > 1 && (
                    <button
                        type="button"
                        onClick={() => setIdx((i) => (i + 1) % photos.length)}
                        className="absolute right-2 lg:right-6 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-12 w-12 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-full transition active:scale-95 backdrop-blur-sm z-10"
                        data-testid="admin-photo-next-btn"
                    >
                        <ChevronRight className="h-6 w-6" />
                    </button>
                )}
            </div>

            {/* Comments + direct AI panel */}
            <div
                className="w-full max-w-3xl mx-auto px-4 mt-4 mb-8 space-y-4"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Direct admin AI panel — always available, also when there are no comments */}
                <div className={`border rounded-md p-4 backdrop-blur-sm ${aiState.directMode ? "bg-[#EFF6FF] border-[#E5A93D]/40" : "bg-[#FFFFFF]/95 border-zinc-200"}`} data-testid="direct-ai-panel">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-[#E5A93D]" />
                            <h3 className="font-display font-bold text-sm tracking-tight">AI-bewerking starten</h3>
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5">Admin · 4K JPG</span>
                        </div>
                        {!aiState.directMode && !aiState.commentId && (
                            <button
                                type="button"
                                onClick={openDirectAiPanel}
                                className="inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-95"
                                data-testid="direct-ai-open-btn"
                            >
                                <Sparkles className="h-3.5 w-3.5" /> Bewerk deze foto met AI
                            </button>
                        )}
                        {aiState.directMode && !aiBusy && !showSlider && (
                            <button
                                type="button"
                                onClick={openDirectAiPanel}
                                className="text-[11px] text-zinc-500 hover:text-zinc-950"
                            >
                                Annuleer
                            </button>
                        )}
                    </div>

                    {!aiState.directMode && !aiState.commentId && (
                        <div className="text-[11px] text-zinc-500 mt-2">
                            Klik om de huidige foto (foto #{idx + 1}) met AI te bewerken — werkt ook als er nog geen feedback van een makelaar is.
                        </div>
                    )}

                    {aiState.directMode && (
                        <div className="mt-3 space-y-3" data-testid="direct-ai-form">
                            {!job && (
                                <>
                                    {aiPresets.length > 0 && (
                                        <div className="space-y-1">
                                            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Preset (optioneel)</div>
                                            <select
                                                value={aiPresetKey}
                                                onChange={(e) => {
                                                    const key = e.target.value;
                                                    setAiPresetKey(key);
                                                    if (!key) return;
                                                    const p = aiPresets.find((x) => x.key === key);
                                                    if (p) {
                                                        setAiState((s) => ({ ...s, directText: p.prompt_nl || "" }));
                                                    }
                                                }}
                                                className="w-full bg-[#FAFAFA] border border-zinc-200 rounded-sm h-10 px-3 text-sm text-zinc-800 focus:outline-none focus:border-[#E5A93D]"
                                                data-testid="direct-ai-preset-select"
                                            >
                                                <option value="">— Kies een snelle preset of typ hieronder —</option>
                                                {[
                                                    { key: "lucht", label: "Lucht" },
                                                    { key: "objecten", label: "Objecten verwijderen" },
                                                    { key: "interieur", label: "Interieur" },
                                                    { key: "tuin", label: "Tuin / buiten" },
                                                    { key: "licht", label: "Licht / sfeer" },
                                                    { key: "overig", label: "Overig" },
                                                ].map((cat) => {
                                                    const items = aiPresets.filter((p) => p.category === cat.key);
                                                    if (items.length === 0) return null;
                                                    return (
                                                        <optgroup key={cat.key} label={cat.label}>
                                                            {items.map((p) => (
                                                                <option key={p.key} value={p.key}>{p.label_nl}</option>
                                                            ))}
                                                        </optgroup>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    )}
                                    <textarea
                                        rows={3}
                                        value={aiState.directText}
                                        onChange={(e) => setAiState((s) => ({ ...s, directText: e.target.value }))}
                                        placeholder="Wat moet er gebeuren? Bv. 'Maak deze berging leeg en propper', 'Lucht vervangen door blauwe lucht', 'Auto weg op de oprit'"
                                        className="w-full bg-[#FAFAFA] border border-zinc-200 rounded-sm p-3 text-sm text-zinc-800 focus:outline-none focus:border-[#E5A93D]"
                                        data-testid="direct-ai-prompt"
                                    />
                                    <textarea
                                        rows={2}
                                        value={aiState.extraText}
                                        onChange={(e) => setAiState((s) => ({ ...s, extraText: e.target.value }))}
                                        placeholder="Optioneel: extra instructies voor de AI (bv. 'behoud schaduwen, hou de stijl consistent')"
                                        className="w-full bg-[#FAFAFA] border border-zinc-100 rounded-sm p-2 text-[12px] text-zinc-600 focus:outline-none focus:border-[#E5A93D]"
                                        data-testid="direct-ai-extra"
                                    />
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className="text-[11px] text-zinc-500">Bron: foto #{idx + 1} · admin-initiated</span>
                                        <button
                                            type="button"
                                            onClick={startAiEdit}
                                            disabled={!aiState.directText.trim()}
                                            className="inline-flex items-center gap-1.5 text-xs px-4 h-9 bg-[#E5A93D] hover:bg-[#C8922C] disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold rounded-sm transition active:scale-95"
                                            data-testid="direct-ai-run-btn"
                                        >
                                            <Sparkles className="h-3.5 w-3.5" /> Voer uit
                                        </button>
                                    </div>
                                </>
                            )}

                            {job && job.optimized_prompt && (
                                <div className="text-[11px] text-zinc-400 bg-black/40 border border-white/5 rounded-sm p-2 font-mono leading-snug">
                                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Geoptimaliseerde prompt</div>
                                    {job.optimized_prompt}
                                </div>
                            )}

                            {job && job.status === "failed" && (
                                <div className="text-xs text-red-700 bg-red-500/10 border border-red-500/30 rounded-sm p-2 flex items-center justify-between gap-2 flex-wrap">
                                    <span>AI mislukt: {job.error || "onbekende fout"}</span>
                                    {(job.fal_raw_url || job.fal_request_id) && (
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    const { data } = await api.post(`/ai-edit-jobs/${job.id}/recover`);
                                                    setAiState((s) => ({ ...s, job: data }));
                                                    toast.success("Resultaat opgehaald");
                                                } catch (e) {
                                                    toast.error(e.response?.data?.detail || "Ophalen mislukt");
                                                }
                                            }}
                                            className="inline-flex items-center gap-1 text-[11px] px-2 h-7 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-95"
                                            data-testid="direct-ai-recover-btn"
                                        >
                                            Ophaal van Fal
                                        </button>
                                    )}
                                </div>
                            )}

                            {showSlider && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <button
                                        type="button"
                                        onClick={approveAiEdit}
                                        className="inline-flex items-center gap-1.5 text-xs px-4 h-9 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-sm transition active:scale-95"
                                        data-testid="direct-ai-approve-btn"
                                    >
                                        <Check className="h-3.5 w-3.5" /> Goedkeuren — vervang foto
                                    </button>
                                    <button
                                        type="button"
                                        onClick={retryAiEdit}
                                        className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95 border border-zinc-200"
                                        data-testid="direct-ai-retry-btn"
                                    >
                                        <RotateCcw className="h-3.5 w-3.5" /> Opnieuw
                                    </button>
                                    <button
                                        type="button"
                                        onClick={rejectAiEdit}
                                        className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-red-500/15 text-red-700 rounded-sm transition active:scale-95 border border-red-500/20"
                                        data-testid="direct-ai-reject-btn"
                                    >
                                        <X className="h-3.5 w-3.5" /> Verwerpen
                                    </button>
                                    {job?.duration_ms && (
                                        <span className="ml-auto text-[11px] font-mono text-zinc-500">
                                            {(job.duration_ms / 1000).toFixed(1)}s
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Kling V3 Pro image-to-video panel — admin only, always available */}
                <div className={`border rounded-md p-4 backdrop-blur-sm ${videoState.open ? "bg-[#0F141A] border-sky-400/40" : "bg-[#FFFFFF]/95 border-zinc-200"}`} data-testid="video-shot-panel">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Film className="h-4 w-4 text-sky-700" />
                            <h3 className="font-display font-bold text-sm tracking-tight">Cinematische videoshot</h3>
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5">Kling V3 Pro · 5s</span>
                            {videoState.history.length > 0 && (
                                <span className="text-[10px] uppercase tracking-wider text-sky-700 bg-sky-500/10 border border-sky-500/30 rounded-sm px-1.5 py-0.5" data-testid="video-shot-history-count">
                                    {videoState.history.length} bewaard
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={toggleVideoPanel}
                            className="inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-sky-500 hover:bg-sky-400 text-black font-bold rounded-sm transition active:scale-95"
                            data-testid="video-shot-toggle-btn"
                        >
                            <Film className="h-3.5 w-3.5" /> {videoState.open ? "Sluit" : "Genereer videoshot"}
                        </button>
                    </div>

                    {!videoState.open && (
                        <div className="text-[11px] text-zinc-500 mt-2">
                            Maak een cinematic 5-seconden video van foto #{idx + 1}: Claude leest de foto, Fal.ai Kling V3 Pro genereert. Typisch 2–5 minuten.
                        </div>
                    )}

                    {videoState.open && (
                        <div className="mt-3 space-y-3" data-testid="video-shot-form">
                            {!videoState.job && (
                                <>
                                    <div className="space-y-1">
                                        <label className="text-[11px] uppercase tracking-wider text-zinc-500">Camera-beweging</label>
                                        <select
                                            value={videoState.presetKey}
                                            onChange={(e) => setVideoState((s) => ({ ...s, presetKey: e.target.value }))}
                                            className="w-full bg-[#FAFAFA] border border-zinc-200 rounded-sm p-2 text-sm text-zinc-800 focus:outline-none focus:border-sky-400"
                                            data-testid="video-shot-preset"
                                        >
                                            <option value="" disabled>— Kies een shot —</option>
                                            <optgroup label="Vrije invoer">
                                                <option value="__custom__">Eigen prompt (rechtstreeks naar Kling)</option>
                                            </optgroup>
                                            {["indoor", "outdoor", "walkthrough", "transition"].map((cat) => {
                                                const items = videoState.presets.filter((p) => p.category === cat);
                                                if (items.length === 0) return null;
                                                const label = {
                                                    indoor: "Indoor",
                                                    outdoor: "Outdoor",
                                                    walkthrough: "Walkthrough (voice-over stijl)",
                                                    transition: "Transition (fade tussen 2 foto's)",
                                                }[cat];
                                                return (
                                                    <optgroup key={cat} label={label}>
                                                        {items.map((p) => (
                                                            <option key={p.key} value={p.key}>{p.label_nl}</option>
                                                        ))}
                                                    </optgroup>
                                                );
                                            })}
                                        </select>
                                    </div>

                                    {/* Always-available start + end image pickers.
                                        For transition presets the end-image is REQUIRED; for any other
                                        preset it's optional (when set Kling treats it as a 2-frame
                                        morph/transition with the chosen camera move). */}
                                    {(() => {
                                        const preset = videoState.presets.find((p) => p.key === videoState.presetKey);
                                        const requiresEnd = !!preset?.requires_end_image;
                                        // Build candidates list — include BOTH the current photo and the
                                        // pre-AI original for any photo that's been edited. This is what
                                        // lets admins build a real "Voor → Na" fade transition: pick
                                        // "Foto #i · Origineel" as start and "Foto #i · AI" as end.
                                        // When 9:16 reel versions exist (current and/or original), they
                                        // are appended right after their landscape counterparts so admin
                                        // can build a 9:16 transition video for reels.
                                        const candidates = [];
                                        (photos || []).forEach((u, i) => {
                                            const orig = previousUrls?.[String(i)];
                                            const reelCur = reelUrls?.[String(i)];
                                            const reelOrig = reelOriginalUrls?.[String(i)];
                                            if (orig) {
                                                candidates.push({ url: orig, label: `Foto #${i + 1}`, sub: "Origineel", idx: i, variant: "original" });
                                                if (reelOrig) {
                                                    candidates.push({ url: reelOrig, label: `Foto #${i + 1}`, sub: "Origineel · 9:16", idx: i, variant: "reel-original" });
                                                }
                                                candidates.push({ url: u, label: `Foto #${i + 1}`, sub: "AI", idx: i, variant: "ai" });
                                                if (reelCur) {
                                                    candidates.push({ url: reelCur, label: `Foto #${i + 1}`, sub: "AI · 9:16", idx: i, variant: "reel-ai" });
                                                }
                                            } else {
                                                candidates.push({ url: u, label: `Foto #${i + 1}`, sub: "", idx: i, variant: "plain" });
                                                if (reelCur) {
                                                    candidates.push({ url: reelCur, label: `Foto #${i + 1}`, sub: "9:16", idx: i, variant: "reel-ai" });
                                                }
                                            }
                                        });

                                        // When the lightbox is in "reel view" the admin clearly wants
                                        // to work with the 9:16 variant — default the videoshot's start
                                        // image to the reel URL so they don't have to manually pick it
                                        // every time. Admin can still override via "kies foto".
                                        const reelDefault = showingReel ? reelUrlForIdx : null;
                                        const effectiveStartUrl = videoState.startImageUrl || reelDefault || photos[idx];
                                        const findCand = (url) => candidates.find((c) => c.url === url);
                                        const startCand = findCand(effectiveStartUrl);
                                        const endCand = videoState.endImageUrl ? findCand(videoState.endImageUrl) : null;

                                        const renderThumb = (c, selected, onClick, testid) => (
                                            <button
                                                key={`${c.url}-${c.variant}`}
                                                type="button"
                                                onClick={onClick}
                                                className={`relative aspect-[4/3] rounded-sm overflow-hidden border-2 transition active:scale-95 ${selected ? "border-sky-400 ring-2 ring-sky-400/40" : "border-zinc-200 hover:border-zinc-400"}`}
                                                data-testid={testid}
                                                title={`${c.label}${c.sub ? " · " + c.sub : ""}`}
                                            >
                                                <img src={`${BACKEND}${c.url}`} alt={c.label} loading="lazy" className="w-full h-full object-cover" />
                                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-1.5">
                                                    <div className="text-[11px] font-medium text-zinc-200 leading-tight">{c.label}</div>
                                                    {c.sub && (
                                                        <div className={`text-[9px] uppercase tracking-wider leading-tight ${(c.variant === "ai" || c.variant === "reel-ai") ? "text-[#E5A93D]" : (c.variant?.startsWith("reel") ? "text-fuchsia-700" : "text-sky-700")}`}>{c.sub}</div>
                                                    )}
                                                </div>
                                                {selected && (
                                                    <span className="absolute top-1.5 right-1.5 bg-sky-400 text-black rounded-full w-5 h-5 inline-flex items-center justify-center shadow-md">
                                                        <Check className="h-3.5 w-3.5" />
                                                    </span>
                                                )}
                                                {c.variant === "ai" && (
                                                    <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 bg-[#E5A93D] text-black text-[9px] font-bold uppercase tracking-wider rounded-sm px-1 py-0.5">
                                                        <Sparkles className="h-2.5 w-2.5" /> AI
                                                    </span>
                                                )}
                                                {c.variant === "reel-ai" && (
                                                    <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 bg-fuchsia-500 text-zinc-950 text-[9px] font-bold uppercase tracking-wider rounded-sm px-1 py-0.5">
                                                        <Film className="h-2.5 w-2.5" /> AI REEL
                                                    </span>
                                                )}
                                                {c.variant === "reel-original" && (
                                                    <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 bg-fuchsia-700 text-zinc-950 text-[9px] font-bold uppercase tracking-wider rounded-sm px-1 py-0.5">
                                                        <Film className="h-2.5 w-2.5" /> ORIG REEL
                                                    </span>
                                                )}
                                            </button>
                                        );

                                        // Group candidates by photo idx for visual pairing (original next to AI)
                                        const grouped = [];
                                        {
                                            let cur = null;
                                            candidates.forEach((c) => {
                                                if (!cur || cur.idx !== c.idx) {
                                                    cur = { idx: c.idx, items: [] };
                                                    grouped.push(cur);
                                                }
                                                cur.items.push(c);
                                            });
                                        }

                                        const pickerMode = videoState.showStartPicker ? "start" : (videoState.showEndPicker ? "end" : null);
                                        const closePicker = () => setVideoState((s) => ({ ...s, showStartPicker: false, showEndPicker: false }));
                                        const pickFromModal = (url) => {
                                            if (pickerMode === "start") {
                                                setVideoState((s) => ({ ...s, startImageUrl: url, showStartPicker: false }));
                                            } else {
                                                setVideoState((s) => ({ ...s, endImageUrl: url, showEndPicker: false }));
                                            }
                                        };
                                        const modalSelectedUrl = pickerMode === "start" ? effectiveStartUrl : videoState.endImageUrl;

                                        return (
                                            <div className="space-y-3" data-testid="video-shot-image-pickers">
                                                {/* Start image */}
                                                <div className="space-y-1">
                                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                                        <label className="text-[11px] uppercase tracking-wider text-zinc-500">Start-foto</label>
                                                        <div className="flex items-center gap-2 text-[11px] text-zinc-600">
                                                            <span className="font-mono">
                                                                {startCand?.label || `Foto #${idx + 1}`}
                                                                {startCand?.sub && <span className={`ml-1 font-bold ${startCand.variant === "ai" ? "text-[#E5A93D]" : "text-sky-700"}`}>· {startCand.sub}</span>}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setVideoState((s) => ({ ...s, showStartPicker: true, showEndPicker: false }))}
                                                                className="text-sky-700 hover:text-sky-700 underline"
                                                                data-testid="video-shot-toggle-start-picker"
                                                            >
                                                                kies foto
                                                            </button>
                                                            {videoState.startImageUrl && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setVideoState((s) => ({ ...s, startImageUrl: "" }))}
                                                                    className="text-zinc-500 hover:text-zinc-800 underline"
                                                                >
                                                                    reset
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {startCand && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setVideoState((s) => ({ ...s, showStartPicker: true, showEndPicker: false }))}
                                                            className="block w-full max-w-xs aspect-[4/3] rounded-sm overflow-hidden border-2 border-zinc-200 hover:border-sky-400/60 transition group"
                                                            data-testid="video-shot-start-preview"
                                                        >
                                                            <div className="relative w-full h-full">
                                                                <img src={`${BACKEND}${thumbUrl(startCand.url, 400)}`} alt="start" className="w-full h-full object-cover" />
                                                                {startCand.variant === "ai" && (
                                                                    <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 bg-[#E5A93D] text-black text-[9px] font-bold uppercase tracking-wider rounded-sm px-1 py-0.5">
                                                                        <Sparkles className="h-2.5 w-2.5" /> AI
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Swap start ↔ end with one click — useful for finding the best direction quickly */}
                                                <div className="flex items-center justify-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => setVideoState((s) => ({
                                                            ...s,
                                                            startImageUrl: s.endImageUrl,
                                                            endImageUrl: s.startImageUrl || photos[idx],
                                                        }))}
                                                        disabled={!videoState.endImageUrl}
                                                        className="inline-flex items-center gap-1.5 text-[11px] px-3 h-7 bg-zinc-100 hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed text-zinc-800 rounded-sm transition active:scale-95 border border-zinc-200"
                                                        data-testid="video-shot-swap-btn"
                                                        title="Verwissel start- en eind-foto"
                                                    >
                                                        <GitCompareArrows className="h-3.5 w-3.5" /> Wissel start ↔ eind
                                                    </button>
                                                </div>

                                                {/* End image */}
                                                <div className="space-y-1">
                                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                                        <label className={`text-[11px] uppercase tracking-wider ${requiresEnd ? "text-sky-700" : "text-zinc-500"}`}>
                                                            Eind-foto {requiresEnd ? "(verplicht voor transition)" : "(optioneel)"}
                                                        </label>
                                                        <div className="flex items-center gap-2 text-[11px] text-zinc-600">
                                                            <span className="font-mono">
                                                                {endCand
                                                                    ? <>{endCand.label}{endCand.sub && <span className={`ml-1 font-bold ${endCand.variant === "ai" ? "text-[#E5A93D]" : "text-sky-700"}`}>· {endCand.sub}</span>}</>
                                                                    : "geen"}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setVideoState((s) => ({ ...s, showEndPicker: true, showStartPicker: false }))}
                                                                className="text-sky-700 hover:text-sky-700 underline"
                                                                data-testid="video-shot-toggle-end-picker"
                                                            >
                                                                kies foto
                                                            </button>
                                                            {videoState.endImageUrl && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setVideoState((s) => ({ ...s, endImageUrl: "" }))}
                                                                    className="text-zinc-500 hover:text-zinc-800 underline"
                                                                >
                                                                    wis
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {endCand ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => setVideoState((s) => ({ ...s, showEndPicker: true, showStartPicker: false }))}
                                                            className="block w-full max-w-xs aspect-[4/3] rounded-sm overflow-hidden border-2 border-zinc-200 hover:border-sky-400/60 transition"
                                                            data-testid="video-shot-end-preview"
                                                        >
                                                            <div className="relative w-full h-full">
                                                                <img src={`${BACKEND}${thumbUrl(endCand.url, 400)}`} alt="end" className="w-full h-full object-cover" />
                                                                {endCand.variant === "ai" && (
                                                                    <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 bg-[#E5A93D] text-black text-[9px] font-bold uppercase tracking-wider rounded-sm px-1 py-0.5">
                                                                        <Sparkles className="h-2.5 w-2.5" /> AI
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </button>
                                                    ) : requiresEnd ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => setVideoState((s) => ({ ...s, showEndPicker: true, showStartPicker: false }))}
                                                            className="w-full max-w-xs aspect-[4/3] rounded-sm border-2 border-dashed border-sky-400/40 hover:border-sky-300 bg-sky-400/5 text-sky-700 text-xs inline-flex items-center justify-center gap-1.5 transition active:scale-[0.98]"
                                                            data-testid="video-shot-end-empty-btn"
                                                        >
                                                            <Sparkles className="h-3.5 w-3.5" /> Kies eind-foto
                                                        </button>
                                                    ) : null}
                                                    {!requiresEnd && videoState.endImageUrl && (
                                                        <div className="text-[10px] text-sky-700">
                                                            Tip: Kling maakt nu een vloeiende beweging van start naar eind-foto.
                                                        </div>
                                                    )}
                                                </div>

                                                {pickerMode && (
                                                    <div
                                                        className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-stretch justify-center"
                                                        onClick={(e) => { if (e.target === e.currentTarget) closePicker(); }}
                                                        data-testid={`video-shot-picker-modal-${pickerMode}`}
                                                    >
                                                        <div className="w-full max-w-6xl bg-[#FFFFFF] border border-zinc-200 rounded-md my-6 mx-4 flex flex-col overflow-hidden shadow-2xl shadow-black/60">
                                                            <div className="flex items-center justify-between gap-3 p-4 border-b border-zinc-200 bg-[#F4F4F5]">
                                                                <div className="flex items-center gap-2">
                                                                    <Film className="h-4 w-4 text-sky-700" />
                                                                    <h3 className="font-display font-bold text-lg tracking-tight">
                                                                        Kies {pickerMode === "start" ? "start-foto" : "eind-foto"}
                                                                    </h3>
                                                                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5">
                                                                        {candidates.length} foto&apos;s
                                                                    </span>
                                                                </div>
                                                                <button onClick={closePicker} className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-zinc-100 hover:bg-zinc-200 text-zinc-950" data-testid={`video-shot-picker-close-${pickerMode}`}>
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                            <div className="flex-1 overflow-y-auto p-4 space-y-5 scrollbar-thin">
                                                                {/* Pairs: original + AI side-by-side (highlighted) */}
                                                                {grouped.filter((g) => g.items.length > 1).map((g) => {
                                                                    const hasAi = g.items.some((it) => it.variant === "ai" || it.variant === "original");
                                                                    const hasReel = g.items.some((it) => it.variant === "reel-ai" || it.variant === "reel-original");
                                                                    let groupLabel;
                                                                    if (hasAi && hasReel) groupLabel = `Foto #${g.idx + 1} · Voor & na AI + reels`;
                                                                    else if (hasAi) groupLabel = `Foto #${g.idx + 1} · Voor & na AI`;
                                                                    else if (hasReel) groupLabel = `Foto #${g.idx + 1} · Landscape & Reel (9:16)`;
                                                                    else groupLabel = `Foto #${g.idx + 1}`;
                                                                    return (
                                                                        <div key={`pair-${g.idx}`}>
                                                                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E5A93D] mb-2">{groupLabel}</div>
                                                                            <div className={`grid ${g.items.length >= 3 ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-2"} gap-3 p-2 bg-[#F4F4F5] border border-[#E5A93D]/20 rounded-sm`}>
                                                                                {g.items.map((c) => renderThumb(
                                                                                    c,
                                                                                    modalSelectedUrl === c.url,
                                                                                    () => pickFromModal(c.url),
                                                                                    `video-shot-${pickerMode}-thumb-${c.idx}-${c.variant}`,
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                                {/* All other photos in one large grid */}
                                                                {(() => {
                                                                    const singles = grouped.filter((g) => g.items.length === 1).flatMap((g) => g.items);
                                                                    if (singles.length === 0) return null;
                                                                    return (
                                                                        <div>
                                                                            {grouped.some((g) => g.items.length > 1) && (
                                                                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Overige foto&apos;s</div>
                                                                            )}
                                                                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                                                                {singles.map((c) => renderThumb(
                                                                                    c,
                                                                                    modalSelectedUrl === c.url,
                                                                                    () => pickFromModal(c.url),
                                                                                    `video-shot-${pickerMode}-thumb-${c.idx}-${c.variant}`,
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {(() => {
                                        const isCustom = videoState.presetKey === "__custom__";
                                        return (
                                            <div className="space-y-1">
                                                <label className={`text-[11px] uppercase tracking-wider ${isCustom ? "text-sky-700" : "text-zinc-500"}`}>
                                                    {isCustom ? "Eigen prompt (verplicht — gaat letterlijk naar Kling)" : "Extra instructie (optioneel)"}
                                                </label>
                                                <textarea
                                                    rows={isCustom ? 5 : 2}
                                                    value={videoState.extraText}
                                                    onChange={(e) => setVideoState((s) => ({ ...s, extraText: e.target.value }))}
                                                    placeholder={isCustom
                                                        ? "Beschrijf de gewenste video. Engels werkt het best. Bv. 'Slow dolly-in toward the front door of the house, golden hour lighting. RED cinema camera quality.'"
                                                        : "Optioneel: extra instructie die letterlijk aan Kling wordt meegegeven (Engels werkt het best)"}
                                                    className={`w-full bg-[#FAFAFA] border ${isCustom ? "border-sky-400/40 focus:border-sky-400" : "border-zinc-100 focus:border-sky-400"} rounded-sm p-2 text-[12px] text-zinc-600 focus:outline-none`}
                                                    data-testid="video-shot-extra"
                                                />
                                                {isCustom && (
                                                    <p className="text-[10px] text-zinc-500">
                                                        Tip: pin de camera-beweging aan het begin (bv. <span className="text-sky-700 font-mono">&quot;A slow pano shot left to right.&quot;</span>) en voeg toe wat Kling moet behouden, anders kan het beeld vervormen.
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className="text-[11px] text-zinc-500">
                                            Bron: {(videoState.startImageUrl ? `start ${(photos || []).indexOf(videoState.startImageUrl) + 1 || "?"}` : `foto #${idx + 1}`)}
                                            {videoState.endImageUrl ? ` → eind ${(photos || []).indexOf(videoState.endImageUrl) + 1 || "?"}` : ""} · 5s · audio uit · directe prompt
                                        </span>
                                        <button
                                            type="button"
                                            onClick={startVideoShot}
                                            disabled={!videoState.presetKey || (videoState.presetKey === "__custom__" && !(videoState.extraText || "").trim())}
                                            className="inline-flex items-center gap-1.5 text-xs px-4 h-9 bg-sky-500 hover:bg-sky-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold rounded-sm transition active:scale-95"
                                            data-testid="video-shot-run-btn"
                                        >
                                            <Film className="h-3.5 w-3.5" /> Genereer 5s video
                                        </button>
                                    </div>
                                </>
                            )}

                            {videoBusy && (
                                <div className="bg-black/40 border border-sky-500/30 rounded-sm p-3 flex flex-col items-center text-center" data-testid="video-shot-busy">
                                    <Loader2 className="h-8 w-8 animate-spin text-sky-700 mb-2" />
                                    <div className="font-display font-bold text-sm">Kling V3 Pro genereert je videoshot…</div>
                                    <div className="text-[11px] text-zinc-500 mt-1">Fal.ai · 5s · directe prompt</div>
                                    <div className="text-[11px] font-mono text-zinc-500 mt-2">{videoState.elapsed}s · meestal 120–300s</div>
                                    <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                                        {/* Annuleer is ALWAYS available so admin can free up the slot
                                            and retry when Fal hangs. */}
                                        <button
                                            type="button"
                                            onClick={() => cancelVideoShot(videoState.job.id)}
                                            className="inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-red-500/15 hover:bg-red-500/25 text-red-700 rounded-sm transition active:scale-95 border border-red-500/30"
                                            data-testid="video-shot-cancel-btn"
                                            title="Stop deze generatie zodat je opnieuw kan proberen"
                                        >
                                            <X className="h-3.5 w-3.5" /> Annuleer
                                        </button>
                                        {videoState.elapsed >= 360 && videoState.job && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => recoverVideoShot(videoState.job.id)}
                                                    className="inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-sky-500 hover:bg-sky-400 text-black font-bold rounded-sm transition active:scale-95"
                                                    data-testid="video-shot-recover-btn"
                                                >
                                                    Ophaal van Fal
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => attachVideoUrl(videoState.job.id)}
                                                    className="inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded-sm transition active:scale-95 border border-zinc-200"
                                                    data-testid="video-shot-attach-url-btn"
                                                >
                                                    Plak Fal URL
                                                </button>
                                                <label className="inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded-sm transition active:scale-95 border border-zinc-200 cursor-pointer" data-testid="video-shot-manual-upload-label">
                                                    <Upload className="h-3.5 w-3.5" /> Upload MP4
                                                    <input
                                                        type="file"
                                                        accept="video/mp4,video/quicktime,.mp4,.mov"
                                                        className="hidden"
                                                        onChange={(e) => {
                                                            const f = e.target.files?.[0];
                                                            if (f) manualUploadVideo(videoState.job.id, f);
                                                            e.target.value = "";
                                                        }}
                                                        data-testid="video-shot-manual-upload-input"
                                                    />
                                                </label>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {videoState.job && videoState.job.optimized_prompt && (
                                <div className="text-[11px] text-zinc-400 bg-black/40 border border-white/5 rounded-sm p-2 font-mono leading-snug" data-testid="video-shot-optimized-prompt">
                                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Prompt naar Kling</div>
                                    {videoState.job.optimized_prompt}
                                </div>
                            )}

                            {videoState.job && videoState.job.status === "failed" && (
                                <div className="text-xs text-red-700 bg-red-500/10 border border-red-500/30 rounded-sm p-2 space-y-2" data-testid="video-shot-failed">
                                    <div>Mislukt: {videoState.job.error || "onbekende fout"}</div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {(videoState.job.fal_raw_url || videoState.job.fal_request_id) && (
                                            <button
                                                type="button"
                                                onClick={() => recoverVideoShot(videoState.job.id)}
                                                className="inline-flex items-center gap-1 text-[11px] px-2 h-7 bg-sky-500 hover:bg-sky-400 text-black font-bold rounded-sm transition active:scale-95"
                                                data-testid="video-shot-recover-failed-btn"
                                            >
                                                Ophaal van Fal
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => attachVideoUrl(videoState.job.id)}
                                            className="inline-flex items-center gap-1 text-[11px] px-2 h-7 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95 border border-zinc-200"
                                            data-testid="video-shot-attach-url-failed-btn"
                                        >
                                            Plak Fal URL
                                        </button>
                                        <label className="inline-flex items-center gap-1 text-[11px] px-2 h-7 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95 border border-zinc-200 cursor-pointer">
                                            <Upload className="h-3 w-3" /> Upload MP4
                                            <input
                                                type="file"
                                                accept="video/mp4,video/quicktime,.mp4,.mov"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const f = e.target.files?.[0];
                                                    if (f) manualUploadVideo(videoState.job.id, f);
                                                    e.target.value = "";
                                                }}
                                                data-testid="video-shot-manual-upload-failed-input"
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={retryVideoShot}
                                            className="inline-flex items-center gap-1 text-[11px] px-2 h-7 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95 border border-zinc-200"
                                            data-testid="video-shot-retry-failed-btn"
                                        >
                                            <RotateCcw className="h-3 w-3" /> Opnieuw
                                        </button>
                                    </div>
                                </div>
                            )}

                            {videoResult && (
                                <div className="space-y-2" data-testid="video-shot-result">
                                    <video
                                        key={videoResult.id}
                                        src={`${BACKEND}${videoResult.result_url}`}
                                        controls
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        className="w-full max-h-[60vh] rounded-sm bg-zinc-50"
                                        data-testid="video-shot-player"
                                    />
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <a
                                            href={`${BACKEND}${videoResult.result_url}?dl=${encodeURIComponent(videoDownloadName())}`}
                                            download={videoDownloadName()}
                                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-sky-500 hover:bg-sky-400 text-black font-bold rounded-sm transition active:scale-95"
                                            data-testid="video-shot-download-btn"
                                        >
                                            <Download className="h-3.5 w-3.5" /> Download MP4
                                        </a>
                                        <button
                                            type="button"
                                            onClick={retryVideoShot}
                                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95 border border-zinc-200"
                                            data-testid="video-shot-retry-btn"
                                        >
                                            <RotateCcw className="h-3.5 w-3.5" /> Genereer opnieuw
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => deleteVideoShot(videoResult.id)}
                                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-red-500/15 text-red-700 rounded-sm transition active:scale-95 border border-red-500/20"
                                            data-testid="video-shot-delete-btn"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" /> Verwijder
                                        </button>
                                        {videoResult.duration_ms && (
                                            <span className="ml-auto text-[11px] font-mono text-zinc-500">
                                                {(videoResult.duration_ms / 1000).toFixed(1)}s · €{(videoResult.cost_eur || 0).toFixed(2)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* History of saved video shots for THIS photo */}
                            {videoState.history.length > 0 && !videoBusy && (
                                <div className="pt-3 border-t border-zinc-200 space-y-2" data-testid="video-shot-history">
                                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Bewaarde shots voor deze foto</div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {videoState.history
                                            .filter((j) => j.status === "completed" && j.result_url && (!videoResult || j.id !== videoResult.id))
                                            .map((j) => (
                                                <div key={j.id} className="relative group bg-black/40 border border-white/10 rounded-sm overflow-hidden" data-testid={`video-shot-history-item-${j.id}`}>
                                                    <video
                                                        src={`${BACKEND}${j.result_url}`}
                                                        muted
                                                        playsInline
                                                        loop
                                                        onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                                                        onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                                                        onClick={() => setVideoState((s) => ({ ...s, job: j, elapsed: (j.duration_ms || 0) / 1000 }))}
                                                        className="w-full aspect-video object-cover cursor-pointer"
                                                    />
                                                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-1.5 flex items-center justify-between gap-1">
                                                        <span className="text-[10px] text-zinc-200 truncate">{j.preset_label_nl || j.preset_key}</span>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); deleteVideoShot(j.id); }}
                                                            className="text-red-700 hover:text-red-700 opacity-0 group-hover:opacity-100 transition"
                                                            title="Verwijderen"
                                                            data-testid={`video-shot-history-delete-${j.id}`}
                                                        >
                                                            <Trash2 className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Existing per-comment feedback panel */}
                {photoComments.length > 0 && (
                <div>
                    <div className="bg-[#FFFFFF]/95 border border-zinc-200 rounded-md p-4 backdrop-blur-sm">
                        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                            <div className="flex items-center gap-2">
                                <MessageCircle className="h-4 w-4 text-[#E5A93D]" />
                                <h3 className="font-display font-bold text-sm tracking-tight">
                                    Feedback op foto {idx + 1} ({photoComments.length})
                                </h3>
                            </div>
                            {photoComments.some((c) => c.annotation_url) && (
                                <button
                                    type="button"
                                    onClick={() => setShowAnnotations((v) => !v)}
                                    className="text-xs text-[#E5A93D] hover:text-[#C8922C]"
                                    data-testid="admin-toggle-overlays-btn"
                                >
                                    {showAnnotations ? "Verberg tekeningen" : "Toon tekeningen"}
                                </button>
                            )}
                        </div>
                        <div className="space-y-2">
                            {photoComments.map((c) => {
                                const isAiTarget = aiState.commentId === c.id;
                                const isAiCommentJob = isAiTarget && job;
                                return (
                                <div
                                    key={c.id}
                                    className={`border rounded-sm p-3 text-sm text-zinc-800 ${isAiTarget ? "bg-[#EFF6FF] border-[#E5A93D]/30" : "bg-[#F4F4F5] border-zinc-100"}`}
                                    data-testid={`admin-lightbox-comment-${c.id}`}
                                >
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <span className="font-medium text-zinc-950">{c.user_name}</span>
                                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5">
                                            {c.user_role === "admin" ? "Admin" : c.user_role === "agent" ? "Makelaar" : "Gast"}
                                        </span>
                                        <span className="text-[11px] text-zinc-500">
                                            · {new Date(c.created_at).toLocaleString("nl-BE")}
                                        </span>
                                        {!isAiTarget && (
                                            <button
                                                type="button"
                                                onClick={() => openAiPanel(c.id)}
                                                className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-[#E5A93D] hover:bg-[#C8922C] text-black px-2.5 h-7 rounded-sm transition active:scale-95"
                                                data-testid={`ai-edit-trigger-${c.id}`}
                                                title="Vraag AI om deze aanpassing uit te voeren"
                                            >
                                                <Sparkles className="h-3.5 w-3.5" /> Voer uit met AI
                                            </button>
                                        )}
                                        {isAiTarget && (
                                            <button
                                                type="button"
                                                onClick={() => openAiPanel(c.id)}
                                                className="ml-auto text-[11px] text-zinc-500 hover:text-zinc-950"
                                            >
                                                Sluit
                                            </button>
                                        )}
                                    </div>
                                    <div className="whitespace-pre-wrap mb-2">{c.text}</div>

                                    {/* Inline AI panel */}
                                    {isAiTarget && (
                                        <div className="mt-3 pt-3 border-t border-zinc-200 space-y-3" data-testid="ai-edit-panel">
                                            {!isAiCommentJob && (
                                                <>
                                                    <div className="text-[11px] uppercase tracking-wider text-[#E5A93D] flex items-center gap-1.5">
                                                        <Sparkles className="h-3 w-3" /> AI-bewerking
                                                    </div>
                                                    <textarea
                                                        rows={2}
                                                        value={aiState.extraText}
                                                        onChange={(e) => setAiState((s) => ({ ...s, extraText: e.target.value }))}
                                                        placeholder="Optionele extra instructies (bv. 'behoud schaduwen, niet te warm')"
                                                        className="w-full bg-[#FAFAFA] border border-zinc-200 rounded-sm p-2 text-sm text-zinc-800 focus:outline-none focus:border-[#E5A93D]"
                                                        data-testid="ai-extra-text"
                                                    />
                                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                                        <span className="text-[11px] text-zinc-500">Bron: foto #{idx + 1} · {c.annotation_url ? "met tekening" : "zonder tekening"}</span>
                                                        <button
                                                            type="button"
                                                            onClick={startAiEdit}
                                                            className="inline-flex items-center gap-1.5 text-xs px-4 h-9 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-95"
                                                            data-testid="ai-edit-run-btn"
                                                        >
                                                            <Sparkles className="h-3.5 w-3.5" /> Voer uit
                                                        </button>
                                                    </div>
                                                </>
                                            )}

                                            {job && job.optimized_prompt && (
                                                <div className="text-[11px] text-zinc-400 bg-black/40 border border-white/5 rounded-sm p-2 font-mono leading-snug" data-testid="ai-optimized-prompt">
                                                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Geoptimaliseerde prompt</div>
                                                    {job.optimized_prompt}
                                                </div>
                                            )}

                                            {job && job.status === "failed" && (
                                                <div className="text-xs text-red-700 bg-red-500/10 border border-red-500/30 rounded-sm p-2 flex items-center justify-between gap-2 flex-wrap">
                                                    <span>AI mislukt: {job.error || "onbekende fout"}</span>
                                                    {(job.fal_raw_url || job.fal_request_id) && (
                                                        <button
                                                            type="button"
                                                            onClick={async () => {
                                                                try {
                                                                    const { data } = await api.post(`/ai-edit-jobs/${job.id}/recover`);
                                                                    setAiState((s) => ({ ...s, job: data }));
                                                                    toast.success("Resultaat opgehaald");
                                                                } catch (e) {
                                                                    toast.error(e.response?.data?.detail || "Ophalen mislukt");
                                                                }
                                                            }}
                                                            className="inline-flex items-center gap-1 text-[11px] px-2 h-7 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-95"
                                                            data-testid={`ai-recover-failed-btn-${c.id}`}
                                                        >
                                                            Ophaal van Fal
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            {showSlider && (
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <button
                                                        type="button"
                                                        onClick={approveAiEdit}
                                                        className="inline-flex items-center gap-1.5 text-xs px-4 h-9 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-sm transition active:scale-95"
                                                        data-testid="ai-approve-btn"
                                                    >
                                                        <Check className="h-3.5 w-3.5" /> Goedkeuren — vervang foto
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={retryAiEdit}
                                                        className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95 border border-zinc-200"
                                                        data-testid="ai-retry-btn"
                                                    >
                                                        <RotateCcw className="h-3.5 w-3.5" /> Opnieuw
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={rejectAiEdit}
                                                        className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-zinc-100 hover:bg-red-500/15 text-red-700 rounded-sm transition active:scale-95 border border-red-500/20"
                                                        data-testid="ai-reject-btn"
                                                    >
                                                        <X className="h-3.5 w-3.5" /> Verwerpen
                                                    </button>
                                                    {job?.duration_ms && (
                                                        <span className="ml-auto text-[11px] font-mono text-zinc-500">
                                                            {(job.duration_ms / 1000).toFixed(1)}s
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                        <div className="mt-3 text-[11px] text-zinc-500">
                            Tip: klik <span className="text-[#E5A93D] font-medium">&quot;Voer uit met AI&quot;</span> bij een opmerking om de bewerking automatisch te genereren, of <span className="text-[#E5A93D] font-medium">sleep een foto</span> in dit venster om de huidige foto manueel te vervangen.
                        </div>
                    </div>
                </div>
                )}

                {/* Reel-video offer modal — shown automatically after a regular
                    videoshot completes when matching 9:16 reels exist. */}
                {reelVideoOffer.open && (
                    <div
                        className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={(e) => { if (e.target === e.currentTarget) declineReelOffer(); }}
                        data-testid="reel-video-offer-modal"
                    >
                        <div className="bg-[#FFFFFF] border border-fuchsia-500/40 rounded-md max-w-md w-full shadow-2xl shadow-black/60 overflow-hidden">
                            <div className="p-5 border-b border-zinc-200 bg-gradient-to-r from-fuchsia-700/20 to-fuchsia-500/10">
                                <div className="flex items-center gap-2 mb-1">
                                    <Film className="h-5 w-5 text-fuchsia-600" />
                                    <h3 className="font-display font-bold text-lg tracking-tight">Reel-versie genereren?</h3>
                                </div>
                                <p className="text-xs text-zinc-500">
                                    De normale videoshot is klaar. Wil je dezelfde prompt nu draaien op de 9:16 reel{reelVideoOffer.end ? "s" : "-versie"} voor je Instagram/TikTok feed?
                                </p>
                            </div>
                            <div className="p-5 space-y-3">
                                <div className="text-[11px] uppercase tracking-wider text-zinc-500">Bron voor de reel-shot</div>
                                <div className="flex items-center gap-3">
                                    <div className="flex-1">
                                        <div className="aspect-[9/16] w-full max-w-[120px] mx-auto rounded-sm overflow-hidden border border-fuchsia-500/30 bg-zinc-50">
                                            <img src={`${BACKEND}${thumbUrl(reelVideoOffer.start, 400)}`} alt="start reel" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="text-center text-[10px] uppercase tracking-wider text-fuchsia-700 mt-1">Start · 9:16</div>
                                    </div>
                                    {reelVideoOffer.end && (
                                        <>
                                            <span className="text-fuchsia-600 text-xl">→</span>
                                            <div className="flex-1">
                                                <div className="aspect-[9/16] w-full max-w-[120px] mx-auto rounded-sm overflow-hidden border border-fuchsia-500/30 bg-zinc-50">
                                                    <img src={`${BACKEND}${thumbUrl(reelVideoOffer.end, 400)}`} alt="end reel" className="w-full h-full object-cover" />
                                                </div>
                                                <div className="text-center text-[10px] uppercase tracking-wider text-fuchsia-700 mt-1">Eind · 9:16</div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="text-[10px] text-zinc-500 italic border-l-2 border-fuchsia-500/40 pl-2">
                                    Prompt: &quot;{(reelVideoOffer.sourceJob?.optimized_prompt || reelVideoOffer.sourceJob?.custom_prompt || reelVideoOffer.sourceJob?.preset_label_nl || "—").slice(0, 140)}&quot;
                                </div>
                            </div>
                            <div className="p-4 border-t border-zinc-200 bg-[#F4F4F5] flex items-center justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={declineReelOffer}
                                    className="h-9 px-4 text-xs rounded-sm bg-zinc-100 hover:bg-zinc-200 border border-zinc-200 text-zinc-600 transition active:scale-95"
                                    data-testid="reel-video-offer-decline"
                                >
                                    Nee, dank je
                                </button>
                                <button
                                    type="button"
                                    onClick={acceptReelOffer}
                                    className="h-9 px-4 text-xs rounded-sm bg-fuchsia-600 hover:bg-fuchsia-500 text-zinc-950 font-bold transition active:scale-95 inline-flex items-center gap-1.5"
                                    data-testid="reel-video-offer-accept"
                                >
                                    <Film className="h-3.5 w-3.5" /> Ja, genereer reel-shot
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
